<template>
    <div class="type-ahead">
        <slot
                name="before"
        />
        <b-form-input
                :id="inputId"
                :disabled="disabled"
                :class="inputClass"
                :placeholder="placeholder"
                :value="searchValue"
                @update="handleSearch"
                @focus="handleFocus"
        />

        <div
                class="results-wrapper"
                v-if="!useScroll && dropdownState"
        >
            <ul class="dropdown-menu" :class="{'p-0': noPadding}">
                <element-loader
                        v-if="isLoading"
                        :loading="true"
                        :height="30"
                />

                <li
                        v-if="searchValue && !isLoading && !collection.length && !this.allowUserValue"
                        :disabled="false"
                        class="dropdown-item"
                >
                    Search by <em>{{ searchValue }}</em> has no results!
                </li>
                <b-dropdown-item
                        v-for="(resource) of Object.values(this.collection.appResources || {})"
                        :key="resource.uuid"
                        @click="handleSetResource(resource)"
                        :class="{ '-smaller': allowDetails }"
                >
                    {{ resourceTitle(resource) }}
                    <span
                            v-if="allowDetails"
                            class="d-block dropdown-item__details"
                    >
                        {{ resourceDetails(resource) }}
                    </span>
                </b-dropdown-item>
                <b-dropdown-item
                        v-if="searchValue && !isLoading && this.allowUserValue"
                        @click="() => handleSetResource(searchValue)"
                >
                    {{ searchValue }}
                </b-dropdown-item>

                <b-dropdown-item
                        v-if="loaded && hasNext"
                        v-b-visible="handleLoadMore"
                >
                    Loading more...
                </b-dropdown-item>
            </ul>
        </div>

        <div
                class="results-wrapper results-wrapper-scroll"
                v-if="useScroll && dropdownState"
        >
            <vue-custom-scrollbar :settings="scrollSettings" class="scroll">
                <ul class="dropdown-menu" :class="{'p-0': noPadding}">
                    <element-loader
                            v-if="isLoading"
                            :loading="true"
                            :height="30"
                    />

                    <li
                            v-if="searchValue && !isLoading && !collection.length && !this.allowUserValue"
                            :disabled="false"
                            class="dropdown-item"
                    >
                        Search by <em>{{ searchValue }}</em> has no results!
                    </li>
                    <b-dropdown-item
                            v-for="(resource) of Object.values(this.collection.appResources || {})"
                            :key="resource.uuid"
                            @click="handleSetResource(resource)"
                            :class="{ '-smaller': allowDetails }"
                    >
                        {{ resourceTitle(resource) }}
                        <span
                            v-if="allowDetails"
                            class="d-block dropdown-item__details"
                        >
                        {{ resourceDetails(resource) }}
                    </span>
                    </b-dropdown-item>
                    <b-dropdown-item
                            v-if="searchValue && !isLoading && this.allowUserValue"
                            @click="() => handleSetResource(searchValue)"
                    >
                        {{ searchValue }}
                    </b-dropdown-item>

                    <b-dropdown-item
                            v-if="loaded && hasNext"
                            v-b-visible="handleLoadMore"
                    >
                        Loading more...
                    </b-dropdown-item>
                </ul>
            </vue-custom-scrollbar>
        </div>

        <slot
                name="after"
        />
    </div>
</template>

<script>
    import VModel from "./../../mixins/VModel";
    import FormErrorsMixin from "./../FormErrors/FormErrorsMixin";
    import ShowNotificationMixin from "./../../mixins/ShowNotificationMixin";
    import AppResourceSearch from '@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch';
    import ApiFilter from '@milestoneslabs/milestones-js-engine/src/Api/ApiFilter';
    import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
    import Engine from '@milestoneslabs/milestones-js-engine/src/Engine';
    import debounce from 'lodash/debounce';
    import isEmpty from 'lodash/isEmpty';
    import AppResourceCollection from '@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection';
    import {clickOutMixin} from 'bootstrap-vue/src/mixins/click-out';

    import './DynamicTypeAhead.scss';

    export const UPDATE_SEARCH_INPUT_RAW_VALUE = 'update-search-input-raw-value';

    export default {
        name: 'DynamicTypeAhead',
        mixins: [
            VModel,
            FormErrorsMixin,
            ShowNotificationMixin,
            clickOutMixin,
        ],
        props: {
            noPadding: {
                type: Boolean,
                default: false,
            },
            resourceType: {
                type: String,
                required: true,
            },
            searchFilter: {
                type: ApiFilter,
                required: false,
            },
            searchField: {
                type: [String, Function],
                default: 'name',
            },
            labelField: {
                type: [String, Function],
                default: 'name',
            },
            placeholderFallback: {
                type: String,
                default: null,
            },
            reducer: {
                type: [String, Function],
                default: 'uuid',
            },
            inputClass: {
                type: [String, Array, Object],
                default: () => ({}),
            },
            allowUserValue: {
                type: Boolean,
                default: false,
            },
            searchClass: {
                type: Function,
                validator(value) {
                    return new value('WildAppResource') instanceof AppResourceSearch;
                },
                required: false,
                default: null,
            },
            disabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            requestFilter: {
                type: ApiFilter,
                default: () => {
                    return new ApiFilter({});
                },
            },
            inputId: {
                type: String,
                default: '',
            },
            pageSize: {
                type: Number,
                default: 7
            },
            useScroll: {
                type: Boolean,
                default: false,
            },
            allowDetails: {
                type: Boolean,
                default: false,
            },
            scrollSettings: {
                type: Object,
                default: () => {
                    return {
                        wheelPropagation: false,
                    };
                }
            },
        },
        data() {
            let search = new AppResourceSearch(this.resourceType);
            if (this.searchClass) {
                search = new this.searchClass(this.resourceType);
            }

            return {
                loading: false,
                search: search,
                collection: new AppResourceCollection(this.resourceType),
                dropdownState: false,
                searchValue: null,
                rawResource: this.value instanceof AppResource ? this.value : null,
                listenForClickOut: true,
                loaded: false,
            };
        },
        computed: {
            isLoading() {
                return this.search.loading || this.loading;
            },
            placeholder() {
                const title = this.getTitle();
                return title ? title : this.placeholderFallback;
            },

            hasNext() {
                const lastPage = Math.ceil(
                    this.search.vPaginator.total / this.search.vPaginator.perPage,
                );

                return lastPage > this.search.vPaginator.page;
            },
        },
        methods: {
            async searchResources(searchValue) {
                const apiFilter = this.searchFilter ? this.searchFilter : this.requestFilter;

                await this.search.setSearchData({
                    pageSize: this.pageSize,
                    filter: apiFilter,
                }, false);

                if (typeof this.searchField === 'function') {
                    this.searchField(this.searchValue, this.search, ApiFilter);
                } else {
                    apiFilter.addParam(this.searchField, searchValue);
                }

                await this.catchAppResourceErrors(async () => {
                    await this.search.search();
                    this.collection = this.search.appResourceCollection;
                    this.loaded = true;
                }, 'loading');
            },
            handleSearch: debounce(async function (searchValue) {
                this.searchValue = searchValue;

                if (!searchValue || (typeof searchValue === 'string' && searchValue.length < 2)) {
                    this.dropdownState = false;
                    return;
                }

                searchValue = searchValue.trim();

                this.dropdownState = true;
                await this.searchResources(searchValue);
            }, 1000),
            resourceTitle(resource) {
                if (typeof this.labelField === 'function') {
                    return this.labelField(resource);
                }

                return resource[this.labelField];
            },
            resourceDetails(resource) {
                return '';
            },
            handleSetResource(resource) {
                this.internalModel = this.reduce(resource);
                this.rawResource = resource;

                this.dropdownState = false;
                this.searchValue = this.getTitle();
            },
            reduce(resource) {
                let reducedResource;
                if (typeof this.reducer === 'function') {
                    reducedResource = this.reducer(resource);
                } else {
                    if (typeof resource === 'string') {
                        reducedResource = resource;
                    } else {
                        reducedResource = resource[this.reducer];
                    }
                }

                return reducedResource;
            },
            async fetchInitialValue() {
                let uuidValidationPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                if (
                    !this.internalModel ||
                    this.internalModel instanceof AppResource ||
                    !uuidValidationPattern.test(this.internalModel)
                ) {
                    return;
                }

                await this.catchAppResourceErrors(async () => {
                    const resourceClass = Engine.appResourceClass(this.resourceType);
                    const resource = new resourceClass({uuid: this.internalModel});

                    await resource.fetch();

                    this.rawResource = resource;
                }, 'loading', (validationErrors, statusCode) => {
                    if (statusCode === 404) {
                        this.showNotification({
                            message: 'Resource is not available or not found!',
                            type: 'error',
                        });
                    }
                });
            },
            getTitle() {
                if (this.rawResource && !Array.isArray(this.rawResource)) {
                    return this.resourceTitle(this.rawResource);
                }

                if (this.allowUserValue && this.internalModel) {
                    return this.internalModel;
                }
            },
            handleFocus() {
                if (
                    this.dropdownState ||
                    !this.searchValue
                ) {
                    return;
                }

                //since we already have a loaded results, let's show them
                if (this.collection.length) {
                    this.dropdownState = true;
                }
            },
            clickOutHandler() {
                this.dropdownState = false;
            },

            async handleLoadMore() {
                await this.search.loadPage(this.search.page + 1);

                let appResourceCollection = new AppResourceCollection(this.resourceType);

                appResourceCollection.push(this.collection.toArray());
                appResourceCollection.push(this.search.appResourceCollection.toArray());

                this.collection = appResourceCollection;
            }
        },

        beforeMount() {
            this.searchValue = this.getTitle();
            this.fetchInitialValue();
        },
    };
</script>
